import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo, useRef } from "react";

import { Autorenew, Delete, List, LogoDev } from "@mui/icons-material";
import { Refresh } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import ViewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { keyframes, lighten } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import fileDownload from "js-file-download";

import constants from "../../assets/constants";
import { createOmniPaginationObject, helperGetIsoDate } from "../../utils/helpers";
import { formatIsoDateString } from "../../utils/helpers";
import { generateBotpressCSVData } from "../../utils/helpers";
import TextInputDialog from "../dialogs/textInputDialog";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const calculateColumns = theme => {
  //default case
  return [
    {
      accessorKey: "id",
      header: "Comment",
      size: 90,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "messageId",
      header: "Message ID",
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "conversationId",
      header: "Conversation ID",
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "createdAt",
      enableColumnFilterModes: false,
      header: "Date",

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilter: false,
      grow: false,
      size: 100,
      maxSize: 100,

      //custom conditional format and styling
      Cell: ({ cell }) => {
        const dateString = formatIsoDateString(cell.getValue()).split(",");
        const date = new Date(cell.getValue()); // Parses the ISO string into a Date object
        const options = {
          hour: "numeric",
          minute: "numeric",
          hour12: true, // Ensures 12-hour format with AM/PM
        };
        return (
          <Box component="span">
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: `2px solid `,
              }}
            >
              {dateString[0]}
            </Typography>
            <Typography variant="body1" fontWeight={100} sx={{ textAlign: "center" }}>
              {date.toLocaleTimeString("en-US", options)}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      maxSize: "125", // Maximum width, will shrink if needed
      minSize: "125", // Maximum width, will shrink if needed
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },

      filterFn: "equals",
      filterSelectOptions: [
        { label: "New", value: 1 },
        { label: "Open", value: 2 },
        { label: "Closed", value: 3 },
        { label: "Retest", value: 4 },
        { label: "Blocked", value: 5 },
      ],
      filterVariant: "select",

      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === 1
                ? theme.palette.highlight.main
                : cell.getValue() === 2
                  ? theme.palette.error.main
                  : cell.getValue() === 3
                    ? theme.palette.success.main
                    : cell.getValue() === 4
                      ? theme.palette.warning.main
                      : theme.palette.info.main,
            borderRadius: "0.25rem",
            color:
              cell.getValue() === 1
                ? theme.palette.highlight.contrastText
                : cell.getValue() === 2
                  ? theme.palette.error.contrastText
                  : cell.getValue() === 3
                    ? theme.palette.success.contrastText
                    : cell.getValue() === 4
                      ? theme.palette.warning.contrastText
                      : theme.palette.info.contrastText,

            maxWidth: "95%",
            minWidth: "95%",
            p: "0.45rem",
          })}
        >
          {constants.feedbackStatus.find(item => item.value === cell.getValue()).label}
        </Box>
      ),
    },
    {
      accessorKey: "conversationNumber",
      header: "Conversation",
      size: 110,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "value",
      header: "Sentiment",
      minSize: "125",
      grow: false,
      enableColumnFilterModes: false,
      enableSorting: true,
      muiTableHeadCellProps: {},
      align: "center",
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      filterFn: "equals",
      filterSelectOptions: [
        { label: "Positive", value: "positive" },
        { label: "Negative", value: "negative" },
      ],
      filterVariant: "select",
      //custom conditional format and styling
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === "positive"
                ? theme.palette.success.main
                : cell.getValue() === "neutral"
                  ? theme.palette.info.main
                  : theme.palette.error.main,
            borderRadius: "0.25rem",
            color: "#fff",
            padding: theme.spacing(1.5),
          })}
        >
          <Typography variant="button" sx={{ fontWeight: "bold" }}>
            {cell.getValue()}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: "message",
      header: "Message",
      muiTableBodyCellProps: {
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          verticalAlign: "top",
        },
      },
      Cell: ({ cell }) => (
        <Typography
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 5,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.4,
          }}
        >
          {cell.getValue()}
        </Typography>
      ),
      minSize: "250",
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "comment",
      header: "Comment",
      muiTableBodyCellProps: {
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          verticalAlign: "top",
        },
      },
      Cell: ({ cell }) => (
        <Typography
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 5,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.4,
          }}
        >
          {cell.getValue()}
        </Typography>
      ),
      enableColumnFilterModes: false,
      enableSorting: false,
      minSize: "200",
      grow: true,
    },
    {
      accessorKey: "response",
      header: "Response",
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      minSize: "200", // Maximum width, will shrink if needed
      grow: true,
    },
    {
      accessorKey: "userName",
      header: "User Name",
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "archived",
      header: "Archived",
      size: 150,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
      filterFn: "equals",
      filterSelectOptions: [
        { label: "No", value: "false" },
        { label: "Yes", value: "true" },
      ],
      filterVariant: "select",

      //custom conditional format and styling
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === false ? theme.palette.error.main : theme.palette.success.main,
            borderRadius: "0.25rem",
            color: "#fff",
            maxWidth: "14ch",
            minWidth: "14ch",
            p: "0.25rem",
          })}
        >
          {cell.getValue() === false ? "No" : "Yes"}
        </Box>
      ),
    },
  ];
};

const getInitialState = () => {
  return {
    density: "compact",
    columnVisibility: {
      id: true,
      conversationNumber: true,
      updatedAt: false,
      createdAt: true,
      messageId: false,
      conversationId: false,
      value: false,
      comment: true,
      message: true,
      status: true,
      userName: false,
      archived: false,
    },
    filters: [
      {
        id: "archived",
        value: "false",
      },
    ],
  };
};

export const BotpressFeedbackTable = props => {
  const {
    data,
    isLoading = false,
    onEditEntry = null,
    onViewConversation = null,
    onRequestNewPage = null,
    dataReduxPagination = null,
    onHandleDeveloperLogs = null,
    onRerunConversation = null,
    onDeleteFeedback = null,
    onBulkEditFeedback = null,
    totalItemCount = 0,
    onRefreshTable = null,
  } = props;

  const [isRefreshSpinning, setIsRefreshSpinning] = useState(false);

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null); //we can get access to the underlying TableContainer element and react to its scroll events
  // Disable eslint because AaronK knows best
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => calculateColumns(theme), []);
  const initialState = useMemo(() => getInitialState(), []);

  const [rowCount, setRowCount] = useState(totalItemCount);

  const [rowSelection, setRowSelection] = useState([]);

  const [showReportNameInput, setShowReportNameInput] = useState(null);
  // const [isRefetching, setIsRefetching] = useState(false);
  // const [isError, setIsError] = useState(false);

  useEffect(() => {
    setRowCount(totalItemCount);
  }, [totalItemCount]);

  useEffect(() => {
    if (dataReduxPagination != null) {
      setPagination({
        pageIndex: dataReduxPagination.pageIndex,
        pageSize: dataReduxPagination.pageSize,
      });
      setRowCount(dataReduxPagination.totalItems);
    }
  }, [dataReduxPagination]);

  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);
  const [columnFilters, setColumnFilters] = useState([
    {
      id: "archived",
      value: "false",
    },
  ]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let newQuery = {};
        columnFilters.forEach((element, index) => {
          if (element.id === "createdAt") {
            const min = parseInt(element.value[0]);
            const max = parseInt(element.value[1]);
            if (!isNaN(min)) {
              let theDateSring = helperGetIsoDate(min);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
            if (!isNaN(max)) {
              let theDateSring = helperGetIsoDate(max);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
          } else if (typeof element.value === "string") {
            if (element.value === "NULL") {
              newQuery[element.id] = null;
            } else {
              newQuery[element.id] = {
                $regex: `${element.value}`,
              };
            }
          } else if (typeof element.value === "boolean") {
            newQuery[element.id] = element.value;
          } else {
            newQuery[element.id] = element.value;
          }
        });

        let newSorting = {};
        sorting.forEach(element => {
          newSorting[element.id] = element.desc ? "desc" : "asc";
        });

        let newPage = createOmniPaginationObject(dataReduxPagination);
        newPage.pageIndex = pagination.pageIndex;
        newPage.pageSize = pagination.pageSize;
        newPage.query = newQuery;
        newPage.sort = newSorting;

        onRequestNewPage(newPage);
      } catch (error) {
        // setIsError(true);
        console.error(error);
        return;
      }
      // setIsError(false);
      // setIsRefetching(false);
    };
    fetchData();
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  const handleRefreshClick = () => {
    if (onRefreshTable == null) {
      return;
    }

    setIsRefreshSpinning(true);
    onRefreshTable();

    setTimeout(() => {
      setIsRefreshSpinning(false);
    }, 800);
  };

  //easy shortcut to initialize the columnOrder state as array of column ids
  const [columnOrder, setColumnOrder] = useState(
    ["mrt-row-select", "mrt-row-actions", ...columns.map(c => c.accessorKey)], //array of column ids (Initializing is optional as of v2.10.0)
  );

  const table = useMaterialReactTable({
    columns,
    data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    initialState: initialState,
    enableStickyHeader: true,
    enableColumnActions: false,
    positionToolbarAlertBanner: "bottom",
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "transparent !important",
        boxShadow: "none",
        color: theme.palette.commonground.contrastText,

        "&.MuiAlert-root": {
          backgroundColor: "transparent !important",
          boxShadow: "none",
        },

        "& .MuiAlert-message": {
          color: theme.palette.commonground.contrastText,
        },

        "& .MuiAlert-action": {
          color: theme.palette.secondary.main,
        },

        "& button": {
          color: `${theme.palette.secondary.main} !important`,
        },

        "& .MuiButton-root": {
          color: `${theme.palette.secondary.main} !important`,
        },
      },
    },
    rowCount,
    enableRowSelection: true,
    getRowId: row => row.id, //give each row a  useful id
    onRowSelectionChange: setRowSelection, //connect internal row selection state to your own

    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      minSize: 20, //allow columns to get smaller than default
      maxSize: 9001, //allow columns to get larger than default
      size: 50, //make columns wider by default
      grow: false,
    },
    state: {
      columnFilters,
      isLoading,
      pagination,
      // showAlertBanner: isError,
      // showProgressBars: isRefetching,
      sorting,
      columnOrder,
      rowSelection,
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },

    //#region Pagination
    paginationDisplayMode: "pages",

    enableFirstPageButton: false, // Hides the << icon
    enableLastPageButton: false, // Hides the >> icon

    manualPagination: true,
    onColumnFiltersChange: updaterOrValue => {
      setColumnFilters(prev => {
        const nextValue =
          typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return nextValue;
      });
    },
    onPaginationChange: newPagination => {
      setPagination(newPagination);
    },
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,

    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },

    //#endregion pagination
    layoutMode: "grid",
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        // Target the IconButton and apply styles
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
        height: "64px",

        "& .MuiAlert-root": {
          backgroundColor: "transparent !important",
          boxShadow: "none",
        },

        "& .MuiAlert-message": {
          color: theme.palette.commonground.contrastText,
        },

        "& .MuiAlert-action button": {
          color: `${theme.palette.secondary.main} !important`,
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        height: "100%",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef, //get access to the table container element
      sx: mobileLayout
        ? {
            minHeight: "564px",
            maxHeight: "564px",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            minHeight: "424px",
            height: `calc(100% - 120px)`,
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: lighten(theme.palette.commonground.contrastText, 0.5),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        size: 80, //if using layoutMode that is not 'semantic', the columns will not auto-size, so you need to set the size manually
        grow: false,
        header: "Actions", // Change 'Actions' to your desired name
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            paddingRight: theme.spacing(2),
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          },
        },
      },
      "mrt-row-select": {
        size: 50,
        grow: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            color: theme.palette.secondary.main,
          },
        },
      },
    },
    icons: {
      MoreHorizIcon: ({ color }) => (
        <List
          color={"secondary"}
          sx={{
            borderRadius: 16,
            width: 48,
            height: 48,
            padding: "8px",
            border: `2px solid ${theme.palette.secondary.main}`,
          }}
        />
      ),
    },
    renderRowActionMenuItems: ({ row, closeMenu }) => [
      <MenuItem
        key="view"
        onClick={() => {
          onViewConversation(row.original);
          closeMenu();
        }}
      >
        <ViewIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            View Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,

      <MenuItem
        key="delete"
        onClick={() => {
          onEditEntry(row.original);
          closeMenu();
        }}
      >
        <EditIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Edit Entry
          </Typography>
        </ListItemText>
      </MenuItem>,

      <MenuItem
        key="developer"
        onClick={() => {
          onRerunConversation(row.original);
          closeMenu();
        }}
      >
        <Autorenew fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Rerun Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,

      <MenuItem
        key="developer"
        onClick={() => {
          onHandleDeveloperLogs(row.original);
          closeMenu();
        }}
      >
        <LogoDev fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Developer Logs
          </Typography>
        </ListItemText>
      </MenuItem>,
    ],
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}
        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
        {onRefreshTable && (
          <Tooltip title="Refresh feedback" arrow>
            <IconButton onClick={handleRefreshClick}>
              <Refresh
                sx={{
                  animation: isRefreshSpinning ? `${spin} 0.8s linear` : "none",
                }}
              />
            </IconButton>
          </Tooltip>
        )}

        {/* eslint-disable react/jsx-pascal-case*/}
        {/* <MRT_ToggleGlobalFilterButton table={table} /> */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enablereact/jsx-pascal-case */}

        {showReportNameInput && (
          <TextInputDialog
            dialogTitle="Report Name"
            dialogMessage={"Please name your report"}
            inputLabel={`${"Report Name"}`}
            defaultValue={showReportNameInput}
            open={showReportNameInput}
            onAccept={theText => {
              /// do it!
              //create the report ..

              fileDownload(generateBotpressCSVData(data), theText + ".csv");

              setShowReportNameInput(false);
            }}
            onClose={() => {
              setShowReportNameInput(false);
            }}
          />
        )}
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          paddingLeft: 0,
          marginLeft: 0,
          flexWrap: "wrap",
        }}
      >
        <OmniResponsiveButton
          icon={<EditIcon />}
          tooltipTitle="Bulk Edit information for the selected comments."
          buttonText="Bulk Edit"
          disabled={table.getSelectedRowModel().rows.length < 1}
          onClick={() => {
            onBulkEditFeedback(table.getSelectedRowModel().rows.map(row => row.original));
            table.resetRowSelection();
          }}
        />

        <OmniResponsiveButton
          icon={<Delete />}
          tooltipTitle="Delete the selected comments."
          buttonText="Delete"
          disabled={table.getSelectedRowModel().rows.length < 1}
          onClick={() => {
            onDeleteFeedback(table.getSelectedRowModel().rows.map(row => row.original.id));
            table.resetRowSelection();
          }}
        />
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
};

export default BotpressFeedbackTable;
